/**
 *  @class
 *  @function Pagination
 *
 *  TRL 26 Oct 2026: load more / infinite now follow the next-page link rendered by snippets/pagination.liquid
 *  (a real <a href>, so pages work without JavaScript), fetch only this section, skip the brand tile and
 *  promotion tiles on appended pages, and update the "Showing X of Y" line and progress bar after each load.
 *  Sold-out last: in-stock products from later pages go above sold-out ones already shown.
 *  Filters and sorting (assets/facets.js) replace the whole grid and this element, which resets the count.
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id="' + id + '"]');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.status = this.querySelector('.trl-loadmore');
    }
    connectedCallback() {
      this.cleanLinks();
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
    // After a filter or sort change the section is rendered with ?section_id=..., which ends up in the
    // page links. Strip it so the links open full pages.
    cleanLinks() {
      this.querySelectorAll('a[href*="section_id="]').forEach((link) => {
        link.setAttribute('href', this.stripParam(link.getAttribute('href'), 'section_id'));
      });
    }
    stripParam(href, key) {
      try {
        const url = new URL(href, document.location.href);
        url.searchParams.delete(key);
        return url.pathname + url.search + url.hash;
      } catch (e) {
        return href;
      }
    }
    nextUrl() {
      const href = this.button ? this.button.getAttribute('href') : null;
      if (!href) return null;
      try {
        const url = new URL(href, document.location.href);
        url.searchParams.set('section_id', this.dataset.section);
        return url.pathname + url.search;
      } catch (e) {
        return href;
      }
    }
    loadMore() {
      let base = this;
      if (!this.button) return;
      this.button.addEventListener('click', function (event) {
        event.preventDefault();
        base.loadProducts();
        return false;
      });
    }
    infinite() {
      let base = this;
      if (!this.button) return;
      base.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }
    loadProducts() {
      let base = this,
        url = base.nextUrl();
      if (base.getAttribute('loading') || !url) {
        return;
      }
      base.setAttribute('loading', true);
      if (base.button) {
        base.button.setAttribute('aria-busy', 'true');
      }
      fetch(url)
        .then(response => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((responseText) => {
          base.renderProducts(responseText, url);
          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('pagination:page-changed', {
              url: url
            });
          }
        })
        .catch(() => {
          // Could not load in place: open the next page normally instead.
          base.removeAttribute('loading');
          if (base.button) {
            base.button.removeAttribute('aria-busy');
          }
          if (base.button && base.classList.contains('pagination-type--loadmore')) {
            window.location.href = base.stripParam(base.button.getAttribute('href'), 'section_id');
          }
        });
    }
    loadBadges() {
      if (typeof StampedFn !== 'undefined' && StampedFn && typeof StampedFn.loadBadges === 'function') {
        StampedFn.loadBadges();
      }
    }
    renderProducts(html, url) {
      let base = this,
        doc = new DOMParser().parseFromString(html, 'text/html'),
        grid_to_replace = doc.querySelector('[data-id="' + base.dataset.section + '"]') || doc.getElementById('product-grid');

      if (!grid_to_replace || !base.grid) {
        if (base.observer) {
          base.observer.unobserve(base);
        }
        base.removeAttribute('loading');
        if (base.button) {
          base.button.classList.add('visually-hidden');
        }
        return;
      }
      // Product items only: the brand tile (page 1 only) and promotion tiles would repeat on appended pages.
      let products = Array.prototype.filter.call(grid_to_replace.children, (el) => {
        return el.classList.contains('column') && !el.classList.contains('promotion-column') && !el.classList.contains('trl-brandtile-col');
      });
      const overlay = Array.prototype.find.call(base.grid.children, (el) => el.classList.contains('loading-overlay'));

      // With "Show sold-out products last" on, in-stock products from the new page go above the sold-out ones
      // already shown, so the whole list stays in stock first, then sold out.
      const soldOutLast = base.grid.hasAttribute('data-trl-soldout-last');
      const isSoldOut = (el) => !!el.querySelector('.badge.out-of-stock');
      const appendProducts = () => {
        const firstSoldOut = soldOutLast ? Array.prototype.find.call(base.grid.children, (el) => el.classList.contains('column') && isSoldOut(el)) : null;
        for (var i = 0; i < products.length; i++) {
          const before = (firstSoldOut && !isSoldOut(products[i])) ? firstSoldOut : overlay;
          if (before) {
            base.grid.insertBefore(products[i], before);
          } else {
            base.grid.appendChild(products[i]);
          }
        }
      };
      const nextPagination = doc.querySelector('pagination-theme[data-section="' + base.dataset.section + '"]') || doc.querySelector('pagination-theme');
      if (this.animations_enabled) {
        appendProducts();
        base.updateStatus(nextPagination);
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.removeAttribute('loading');
            base.loadBadges();
          }
        });
      } else {
        // No GSAP reveal: cross-fade the newly appended products with the
        // View Transitions API, matching the facet re-render.
        this.renderWithViewTransition(appendProducts);
        base.updateStatus(nextPagination);
        base.removeAttribute('loading');
        base.loadBadges();
      }
    }
    // Copies the count, bar and next link from the fetched page. On the last page the button goes
    // and the line reads "You've seen all Y products".
    updateStatus(nextPagination) {
      const base = this;
      const nextStatus = nextPagination ? nextPagination.querySelector('.trl-loadmore') : null;
      const nextButton = nextPagination ? nextPagination.querySelector('.load-more') : null;
      if (base.button) {
        base.button.removeAttribute('aria-busy');
      }
      if (base.status && nextStatus) {
        const count = base.status.querySelector('.trl-loadmore__count');
        const nextCount = nextStatus.querySelector('.trl-loadmore__count');
        const fill = base.status.querySelector('.trl-loadmore__fill');
        const nextFill = nextStatus.querySelector('.trl-loadmore__fill');
        if (count && nextCount && count.textContent !== nextCount.textContent) {
          count.textContent = nextCount.textContent;
        }
        if (fill && nextFill) {
          fill.style.width = nextFill.style.width;
        }
        base.status.dataset.shown = nextStatus.dataset.shown || '';
        base.status.dataset.total = nextStatus.dataset.total || '';
        base.status.classList.toggle('is-done', nextStatus.classList.contains('is-done'));
      }
      if (nextButton && base.button) {
        base.button.setAttribute('href', base.stripParam(nextButton.getAttribute('href'), 'section_id'));
      } else {
        // Last page reached.
        if (base.observer) {
          base.observer.unobserve(base);
        }
        if (base.button) {
          const hadFocus = document.activeElement === base.button;
          base.button.remove();
          base.button = null;
          if (hadFocus && base.status) {
            const count = base.status.querySelector('.trl-loadmore__count');
            if (count) count.focus({ preventScroll: true });
          }
        }
        if (base.status && !nextStatus) {
          base.status.classList.add('is-done');
        }
      }
    }
    renderWithViewTransition(update) {
      const canTransition = typeof document.startViewTransition === 'function' &&
        document.body.classList.contains('animations-true') &&
        !document.body.classList.contains('open-cc');
      if (canTransition) {
        document.startViewTransition(update);
      } else {
        update();
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}
